import {
  errorUnlessOk,
  buildRequestWithAuth,
  headersBackendSeguros,
} from "./utils";
import environment from "~libs/environment";
import { format } from "date-fns";
import {
  DateRange,
  GetReporteEmisionesResponse,
  GetReporteAnulacionesResponse,
  GetReporteSiniestrosRequest,
  GetReporteSiniestrosResponse,
  GetReporteNovedadesRequest,
  GetReporteNovedadesResponse,
} from "./types";

// El DateRangePicker deja Date en el store, pero el estado inicial de algunos filtros es "".
const toDate = (value?: Date | string | null): Date | null => {
  if (!value) return null;
  const date =
    value instanceof Date ? new Date(value.getTime()) : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const reportes = () => {
  return {
    getEmisiones: (data: DateRange): Promise<GetReporteEmisionesResponse> => {
      const request = buildRequestWithAuth("GET", null, headersBackendSeguros);

      const url = `${environment.segurosUrl}/reportes/emisiones?Desde=${format(
        data.fechaDesde,
        "yyyy'-'MM'-'dd"
      )}&Hasta=${format(data.fechaHasta, "yyyy'-'MM'-'dd")}`;

      return fetch(url, request)
        .then(errorUnlessOk)
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
          throw error;
        });
    },

    getEmisionesDeComisiones: (
      data: DateRange
    ): Promise<GetReporteEmisionesResponse> => {
      const request = buildRequestWithAuth("GET", null, headersBackendSeguros);

      const url = `${environment.segurosUrl}/reportes/emisiones-comisiones?Desde=${format(
        data.fechaDesde,
        "yyyy'-'MM'-'dd"
      )}&Hasta=${format(data.fechaHasta, "yyyy'-'MM'-'dd")}`;

      return fetch(url, request)
        .then(errorUnlessOk)
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
          throw error;
        });
    },

    getAnulaciones: (
      data: DateRange
    ): Promise<GetReporteAnulacionesResponse> => {
      const request = buildRequestWithAuth("GET", null, headersBackendSeguros);

      const url = `${
        environment.segurosUrl
      }/reportes/anulaciones?Desde=${format(
        data.fechaDesde,
        "yyyy'-'MM'-'dd"
      )}&Hasta=${format(data.fechaHasta, "yyyy'-'MM'-'dd")}`;

      return fetch(url, request)
        .then(errorUnlessOk)
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
          throw error;
        });
    },

    getSiniestros: (
      data: GetReporteSiniestrosRequest
    ): Promise<GetReporteSiniestrosResponse> => {
      const dateWithoutTimezone = (date: Date) => {
        const tzoffset = date.getTimezoneOffset() * 60000; // offset in milliseconds
        const withoutTimezone = new Date(date.valueOf() - tzoffset);
        return withoutTimezone;
      };

      const fechaDesdeUTCClear = data.fechaDesde
        ? dateWithoutTimezone(data.fechaDesde)
        : "";
      const fechaHastaUTCClear = data.fechaHasta
        ? dateWithoutTimezone(data.fechaHasta)
        : "";

      const request = buildRequestWithAuth("GET", null, headersBackendSeguros);
      let parameters = `Estado=${data.estado}`;

      if (data.searchCriteria) {
        parameters += `&SearchCriteria=${data.searchCriteria}`;
      }
      if (data.tipo && data.tipo !== "Seleccionar") {
        parameters += `&Tipo=${data.tipo}`;
      }
      if (fechaDesdeUTCClear) {
        parameters += `&FechaDesde=${fechaDesdeUTCClear.toISOString()}`;
      }

      if (fechaHastaUTCClear) {
        fechaHastaUTCClear.setUTCHours(23, 59, 59);
        parameters += `&FechaHasta=${fechaHastaUTCClear.toISOString()}`;
      }

      const url = `${environment.segurosUrl}/reportes/siniestros?${parameters}`;

      return fetch(url, request)
        .then(errorUnlessOk)
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
          throw error;
        });
    },

    getNovedades: (
      data: GetReporteNovedadesRequest
    ): Promise<GetReporteNovedadesResponse> => {
      const request = buildRequestWithAuth("GET", null, headersBackendSeguros);

      // Mismo armado de filtros que el listado (ver api/novedades.ts): el Excel tiene que
      // traer exactamente las filas que el usuario está viendo en pantalla.
      const parameters = new URLSearchParams();

      if (data.negocio) parameters.append("Negocio", data.negocio);
      if (data.compania) parameters.append("Compania", data.compania);
      if (data.tipoNovedad) parameters.append("TipoNovedad", data.tipoNovedad);
      if (data.searchCriteria)
        parameters.append("SearchCriteria", data.searchCriteria);

      const fechaNovedadDesde = toDate(data.fechaNovedadDesde);
      if (fechaNovedadDesde)
        parameters.append("FechaNovedadDesde", fechaNovedadDesde.toISOString());

      const fechaNovedadHasta = toDate(data.fechaNovedadHasta);
      if (fechaNovedadHasta) {
        fechaNovedadHasta.setHours(23, 59, 59);
        parameters.append("FechaNovedadHasta", fechaNovedadHasta.toISOString());
      }

      const inicioVigenciaDesde = toDate(data.inicioVigenciaDesde);
      if (inicioVigenciaDesde)
        parameters.append(
          "InicioVigenciaDesde",
          inicioVigenciaDesde.toISOString()
        );

      const inicioVigenciaHasta = toDate(data.inicioVigenciaHasta);
      if (inicioVigenciaHasta) {
        inicioVigenciaHasta.setHours(23, 59, 59);
        parameters.append(
          "InicioVigenciaHasta",
          inicioVigenciaHasta.toISOString()
        );
      }

      const url = `${environment.segurosUrl}/reportes/novedades?${parameters}`;

      return fetch(url, request)
        .then(errorUnlessOk)
        .then((response) => response.json())
        .catch((error) => {
          console.error(error);
          throw error;
        });
    },
  };
};

export { reportes };
